import React, {useEffect, useState} from "react";
import "../../styles/frontend/about/style.css";

import {Helmet} from "react-helmet-async";
import FirstOurVisionSection from "../../components/OurVision/FirstOurVisionSection";
import NewsletterSection from '../../components/NewsletterSection';

const FirstAboutSection = () => {

    const [popupType, setPopupType] = useState(null);

    const openPopup = (type) => {
        setPopupType(type);
    };

    const closePopup = () => {
        setPopupType(null);
    };

    useEffect(() => {
        const faders = document.querySelectorAll(".fade-in");

        const appearOptions = {
            threshold: 0.1,
            rootMargin: "0px 0px -50px 0px",
        };

        const appearOnScroll = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                entry.target.classList.add("visible");
                observer.unobserve(entry.target);
            });
        }, appearOptions);

        faders.forEach((fader) => {
            appearOnScroll.observe(fader);
        });
    }, []);

    return (
        <>
            <Helmet>
                {/* Seiten-Titel */}
                <title>Unsere Vision - Cloudtal</title>

                {/* Meta-Informationen */}
                <meta
                    name="description"
                    content="Unsere Vision bei Cloudtal: Open-Source-Serverless-Dienste, DSGVO-konform und nachhaltig gehostet in Deutschland. Erfahren Sie mehr über unsere Zukunftspläne."
                />
                <meta
                    name="keywords"
                    content="Cloud-Vision, Serverless, Open Source, Nachhaltigkeit, DSGVO, PostgreSQL, Managed Cloud, Cloudtal"
                />
                <meta name="author" content="Cloudtal Team" />

                {/* Open Graph für Social Media */}
                <meta property="og:title" content="Unsere Vision - Cloudtal" />
                <meta
                    property="og:description"
                    content="Wir setzen auf Open-Source-Technologien und Serverless-Architekturen, um nachhaltige, sichere und leistungsstarke Cloud-Dienste bereitzustellen."
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.cloudtal.de/vision" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://www.cloudtal.de/vision" />
            </Helmet>

            <main className="flex-1">
                {/* Hero Section */}
                <FirstOurVisionSection />

                {/* Unsere Vision Section */}
                <section className="w-full py-12 md:py-24 lg:py-32 bg-zinc-900">
                    <div className="container px-4 md:px-6 text-center">
                        <div className="mx-auto grid max-w-5xl gap-6 py-12 lg:grid-cols-2">
                            <div className="flex flex-col items-center bg-zinc-800 p-6 rounded-lg shadow-md fade-in cursor-pointer" onClick={() => openPopup('serverlessInfo')}>
                                <h3 className="text-xl font-bold text-white">Serverless Open-Source-Dienste</h3>
                                <p className="text-zinc-400 text-center mt-2">
                                    Wir setzen auf Open-Source-Technologien und bieten vollständig verwaltete,
                                    serverlose Dienste an. Dies ermöglicht eine hochskalierbare und effiziente
                                    Cloud-Nutzung – ohne komplexe Infrastruktur.
                                </p>
                            </div>
                            <div className="flex flex-col items-center bg-zinc-800 p-6 rounded-lg shadow-md fade-in cursor-pointer">
                                <h3 className="text-xl font-bold text-white">Unsere ersten Produkte</h3>
                                <p className="text-zinc-400 text-center mt-2">
                                    Unser erstes Produkt, <strong>PostgreSQL</strong>, ist erst der Anfang.
                                    Bald folgen weitere Cloud-Dienste wie <strong>Keycloak</strong>,
                                    alle DSGVO-konform und auf Hochverfügbarkeit ausgelegt.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {popupType === 'serverlessInfo' && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
                        <div className="bg-zinc-900 rounded-lg p-8 max-w-lg w-full text-center shadow-xl mx-4">
                            <h3 className="text-3xl font-bold mb-4 text-zinc-100">Serverless Open-Source-Dienste</h3>
                            <p className="text-zinc-400 mb-6">
                                Unsere serverlosen Open-Source-Dienste bieten maximale Skalierbarkeit, geringe
                                Betriebskosten und höchste Verfügbarkeit. Durch Automatisierung und
                                serverlose Technologien entfällt der Administrationsaufwand, während
                                Ihre Anwendungen reibungslos in der Cloud laufen.
                            </p>
                            <button
                                onClick={closePopup}
                                className="mt-4 bg-zinc-800 text-zinc-200 px-6 py-2 rounded transition-transform duration-300 hover:scale-105"
                            >
                                Schließen
                            </button>
                        </div>
                    </div>
                )}

                {/* Nachhaltigkeit bei Cloudtal */}
                <section className="w-full py-12 md:py-24 lg:py-32 bg-black">
                    <div className="container px-4 md:px-6 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-white">
                                Nachhaltigkeit bei Cloudtal
                            </h2>
                            <p className="max-w-[900px] text-zinc-400 md:text-xl">
                                Unsere Cloud-Dienste sind nicht nur leistungsstark, sondern auch umweltfreundlich.
                                Wir betreiben unsere Infrastruktur mit <strong>100% erneuerbarer Energie</strong> und
                                setzen auf effiziente, ressourcenschonende Architekturen.
                            </p>
                        </div>
                        <div className="mx-auto grid max-w-5xl gap-6 py-12 lg:grid-cols-3">
                            {/* Nachhaltigkeit 1 */}
                            <div className="flex flex-col items-center bg-zinc-800 p-6 rounded-lg shadow-md fade-in">
                                <h3 className="text-xl font-bold text-white">100% Ökostrom</h3>
                                <p className="text-zinc-400 text-center mt-2">
                                    Unsere Cloud wird ausschließlich mit erneuerbaren Energien betrieben.
                                </p>
                            </div>

                            {/* Nachhaltigkeit 2 */}
                            <div className="flex flex-col items-center bg-zinc-800 p-6 rounded-lg shadow-md fade-in">
                                <h3 className="text-xl font-bold text-white">Ressourcenschonend</h3>
                                <p className="text-zinc-400 text-center mt-2">
                                    Durch Serverless-Technologie verbrauchen wir nur, was wirklich benötigt wird.
                                </p>
                            </div>

                            {/* Nachhaltigkeit 3 */}
                            <div className="flex flex-col items-center bg-zinc-800 p-6 rounded-lg shadow-md fade-in">
                                <h3 className="text-xl font-bold text-white">Grünes Hosting</h3>
                                <p className="text-zinc-400 text-center mt-2">
                                    Alle Daten werden in deutschen Rechenzentren mit hohen Umweltstandards gehostet.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Call to Action */}
                <section className="w-full py-12 md:py-24 lg:py-32 bg-zinc-900">
                    <div className="container px-4 md:px-6 text-center">
                        <div className="flex flex-col items-center space-y-4">
                            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-white">
                                Begleiten Sie uns auf dem Weg in die Zukunft der Cloud
                            </h2>
                            <p className="max-w-[700px] text-zinc-400 md:text-xl">
                                Melden Sie sich für unseren Newsletter an und erhalten Sie exklusive Updates
                                zu neuen Cloud-Diensten, Nachhaltigkeitsinitiativen und zur Beta.
                            </p>
                            <button
                                onClick={() => openPopup('newsletter')}
                                className="bg-green-500 text-white px-6 py-3 rounded-lg transition duration-300 hover:bg-green-600"
                            >
                                Jetzt anmelden
                            </button>
                        </div>
                    </div>

                    {/* Popups */}
                    {popupType === 'newsletter' && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
                            <div className="bg-zinc-900 rounded-lg p-8 max-w-lg w-full text-center shadow-xl mx-4">
                                <h3 className="text-3xl font-bold mb-4 text-zinc-100">Exklusives Beta-Tester-Programm</h3>
                                <p className="mb-6 text-zinc-400">
                                    Testen Sie als einer der wenigen unsere Beta-Version und erleben Sie die neuesten Funktionen vor allen anderen. Die Teilnehmerzahl ist streng limitiert – sichern Sie sich jetzt Ihren Platz!
                                </p>
                                <NewsletterSection
                                    title=""
                                    description=""
                                    placeholder="Ihre E-Mail-Adresse"
                                    buttonText="Jetzt registrieren"
                                    privacyText="Wir respektieren Ihre Privatsphäre. Abmeldung jederzeit möglich."
                                    additionalClassName="bg-bottom text-white !p-0 mb-4"
                                />
                                <button
                                    onClick={closePopup}
                                    className="mt-4 bg-zinc-800 text-zinc-200 px-6 py-2 rounded transition-transform duration-300 hover:scale-105"
                                >
                                    Schließen
                                </button>
                            </div>
                        </div>
                    )}

                </section>
            </main>
        </>
    );
};

export default FirstAboutSection;
